/**
 * DynamicManifest Component
 *
 * Dynamically generates and applies a PWA web app manifest
 * based on tenant branding (company name, theme color, uploaded icons).
 * Falls back to the static build-time icons if the tenant has not uploaded any.
 */

import { useEffect, useRef, useState } from 'react';
import { useBranding } from '@/hooks/useBranding';
import { DEFAULT_BRANDING } from '@/config/branding';
import { api } from '@/lib/api';

interface ManifestIcon {
  src: string;
  sizes: string;
  type: string;
  purpose: string;
}

const FALLBACK_ICONS: ManifestIcon[] = [
  { src: '/icons/icon-72x72.png', sizes: '72x72', type: 'image/png', purpose: 'maskable any' },
  { src: '/icons/icon-96x96.png', sizes: '96x96', type: 'image/png', purpose: 'maskable any' },
  { src: '/icons/icon-128x128.png', sizes: '128x128', type: 'image/png', purpose: 'maskable any' },
  { src: '/icons/icon-144x144.png', sizes: '144x144', type: 'image/png', purpose: 'maskable any' },
  { src: '/icons/icon-152x152.png', sizes: '152x152', type: 'image/png', purpose: 'maskable any' },
  { src: '/icons/icon-192x192.png', sizes: '192x192', type: 'image/png', purpose: 'maskable any' },
  { src: '/icons/icon-384x384.png', sizes: '384x384', type: 'image/png', purpose: 'maskable any' },
  { src: '/icons/icon-512x512.png', sizes: '512x512', type: 'image/png', purpose: 'maskable any' },
];

interface BrandingAssetsResponse {
  pwaIcons?: Array<{ src: string; sizes: string; type: string; purpose?: string }>;
}

function mimeFromUrl(url: string): string {
  return url.toLowerCase().includes('.webp') ? 'image/webp' : 'image/png';
}

function resolveIcons(
  uploaded: ManifestIcon[] | undefined,
  logoUrl: string | undefined,
): ManifestIcon[] {
  const custom = uploaded && uploaded.length > 0
    ? uploaded
    : logoUrl
      ? [{ src: logoUrl, sizes: '512x512', type: mimeFromUrl(logoUrl), purpose: 'maskable any' }]
      : undefined;

  if (!custom || custom.length === 0) {
    return FALLBACK_ICONS;
  }

  const bySize = new Map(custom.map((icon) => [icon.sizes, icon]));
  const fallbackCustom = custom[custom.length - 1] ?? custom[0];
  if (!fallbackCustom) {
    return FALLBACK_ICONS;
  }

  return FALLBACK_ICONS.map((entry) => {
    const match = bySize.get(entry.sizes) ?? fallbackCustom;
    return {
      ...entry,
      src: match.src,
      type: match.type,
    };
  });
}

export function DynamicManifest() {
  const { companyName, colors, logo, branding } = useBranding();
  const blobUrlRef = useRef<string | null>(null);
  const [remoteIcons, setRemoteIcons] = useState<ManifestIcon[] | undefined>(undefined);

  useEffect(() => {
    let cancelled = false;
    void api
      .get<BrandingAssetsResponse>('/tenants/branding-assets')
      .then((data) => {
        if (cancelled) return;
        const icons = (data.pwaIcons ?? []).map((icon) => ({
          src: icon.src,
          sizes: icon.sizes,
          type: icon.type,
          purpose: icon.purpose ?? 'maskable any',
        }));
        setRemoteIcons(icons);
      })
      .catch(() => {
        if (!cancelled) setRemoteIcons(undefined);
      });
    return () => {
      cancelled = true;
    };
  }, [branding]);

  useEffect(() => {
    const name = companyName || DEFAULT_BRANDING.companyName;
    const themeColor = colors.primary?.main || DEFAULT_BRANDING.colors.primary.main;
    const icons = resolveIcons(remoteIcons, logo?.url);

    const manifest = {
      name,
      short_name: name.length > 12 ? name.substring(0, 12) : name,
      description: name,
      theme_color: themeColor,
      background_color: '#ffffff', // design-allow-hex: PWA manifest splash background, fixed white (not theme-driven)
      display: 'standalone',
      orientation: 'any',
      start_url: '/',
      scope: '/',
      icons,
      categories: ['business', 'productivity'],
    };

    const blob = new Blob([JSON.stringify(manifest)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);

    // Revoke previous blob URL
    if (blobUrlRef.current) {
      URL.revokeObjectURL(blobUrlRef.current);
    }
    blobUrlRef.current = url;

    // Find or create manifest link
    let link = document.querySelector<HTMLLinkElement>('link[rel="manifest"]');
    if (!link) {
      link = document.createElement('link');
      link.rel = 'manifest';
      document.head.appendChild(link);
    }
    link.href = url;

    // Update apple-mobile-web-app-title
    let appleMeta = document.querySelector<HTMLMetaElement>('meta[name="apple-mobile-web-app-title"]');
    if (appleMeta) {
      appleMeta.content = name;
    } else {
      appleMeta = document.createElement('meta');
      appleMeta.name = 'apple-mobile-web-app-title';
      appleMeta.content = name;
      document.head.appendChild(appleMeta);
    }

    return () => {
      if (blobUrlRef.current) {
        URL.revokeObjectURL(blobUrlRef.current);
        blobUrlRef.current = null;
      }
    };
  }, [companyName, colors.primary?.main, remoteIcons, logo?.url]);

  return null;
}
